import { Link } from 'react-router-dom';
import { ZitaLogo, ZitaWordmark } from '@/components/ZitaLogo';
import { PublicFooter, SUPPORT_EMAIL } from '@/components/PublicFooter';
import { Search, Sparkles, Layers, ShieldCheck } from 'lucide-react';

// About Zita — public page. Reachable from every footer per certification checklist.
const About = () => (
  <div className="min-h-screen flex flex-col">
    <header className="max-w-6xl w-full mx-auto flex items-center justify-between px-4 sm:px-8 py-6">
      <Link to="/" className="flex items-center gap-3">
        <ZitaLogo size={44} />
        <div className="flex flex-col leading-none">
          <ZitaWordmark className="text-3xl" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-zita-mocha mt-0.5">
            Powered by <span className="remod-accent font-semibold">RemodWorks</span>
          </span>
        </div>
      </Link>
      <nav className="flex items-center gap-3">
        <Link to="/login" className="text-zita-mocha text-sm hover:text-zita-espresso">
          Sign in
        </Link>
        <Link to="/register" className="zita-btn-primary text-sm">
          Start 3-day trial
        </Link>
      </nav>
    </header>

    <main className="flex-1">
      <article className="max-w-3xl mx-auto px-4 sm:px-8 py-12 space-y-10 text-zita-espresso">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-zita-mocha mb-3">
            About Zita
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-[1.1]">
            The AI-powered acquisition engine.
          </h1>
          <p className="mt-6 text-lg text-zita-mocha leading-relaxed">
            Zita is a universal search and discovery platform. Tell Zita what
            you&apos;re looking for and its intelligence layer —{' '}
            <span className="ari-accent font-semibold">Ari</span> — figures out
            how to search across supported marketplaces, retailers, social groups,
            business directories, auction sites, job boards, and other
            opportunity sources, then pulls the matching results back into one
            place.
          </p>
          <p className="mt-4 text-lg text-zita-mocha leading-relaxed">
            You don&apos;t need to know Boolean operators, advanced Google-search
            tricks, which websites to check, or which groups to join. That&apos;s
            Ari&apos;s job. Yours is deciding what to do with what Ari brings back.
          </p>
        </div>

        <section className="grid sm:grid-cols-2 gap-4">
          <Card
            icon={Search}
            title="Universal search"
            body="Products, posts, businesses, jobs, services, deals, wanted ads, group posts, leads — Zita is not limited to one category. Describe your search in plain English and Ari runs it across whichever supported sources fit the intent."
          />
          <Card
            icon={Sparkles}
            title="Ari — the intelligence layer"
            body="Ari interprets natural language, expands synonyms and related terms, picks the right sources, and organises results with facts, estimates (labelled with confidence), and clearly-labelled unknowns. Ari also answers general-purpose questions."
          />
          <Card
            icon={Layers}
            title="Source-agnostic architecture"
            body="New supported sources plug in without rebuilding the scanner. Marketplaces, retailers, auctions, groups, directories, job boards — the search pipeline is driven by user intent and each source's capabilities, not by a hardcoded whitelist."
          />
          <Card
            icon={ShieldCheck}
            title="Honest status, honest results"
            body="Every source shows its real status: connected, requires the extension, session expired, blocked, or not yet supported. No fake scans. No simulated progress. Results always link back to the original source so you act directly through the original site or group."
          />
        </section>

        <section className="zita-card p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-zita-sand text-zita-cocoa flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <h2 className="font-serif text-2xl">Built on trust and transparency</h2>
          </div>
          <ul className="space-y-2 text-sm text-zita-mocha leading-relaxed">
            <li>· Zita respects each source&apos;s permissions and terms of access. It never bypasses authentication.</li>
            <li>· Password fields for Facebook, OfferUp, Nextdoor never leave your browser — the extension uses your own signed-in session.</li>
            <li>· AI outputs are labelled. Facts, estimates (with confidence), and unknowns are shown separately.</li>
            <li>· No fake scans, no simulated progress, no placeholder listings.</li>
            <li>· Backend-enforced access control. 3-day free trial · no credit card required at signup.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Zita and RemodWorks</h2>
          <p className="text-sm text-zita-mocha leading-relaxed">
            Zita is a product of RemodWorks. Its job is to <em>find</em> opportunities.
            RemodWorks&apos; business-management platform (RMD) is where users
            <em> operate</em> around the opportunities Zita surfaces. The two
            complement each other — Zita for acquisition, RMD for management —
            with Ari as the shared intelligence layer.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl mb-3">Contact &amp; Support</h2>
          <p className="text-sm text-zita-mocha leading-relaxed">
            Questions about your account, billing, subscription, technical issues, or general
            support — email us any time at{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-zita-cocoa hover:underline font-medium"
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </section>
      </article>
    </main>

    <PublicFooter />
  </div>
);

const Card = ({ icon: Icon, title, body }) => (
  <div className="zita-card p-5">
    <div className="w-10 h-10 rounded-full bg-zita-sand text-zita-cocoa flex items-center justify-center mb-3">
      <Icon size={18} />
    </div>
    <div className="font-serif text-xl text-zita-espresso">{title}</div>
    <p className="mt-2 text-sm text-zita-mocha leading-relaxed">{body}</p>
  </div>
);

export default About;
